/**
 * MBA Lab — the MBA Program Shortlist Builder's criteria and tier vocabulary.
 * Pure data plus tiny validators, matching interview-questions.js /
 * readiness-dimensions.js's precedent (data file separate from the model that
 * consumes it).
 *
 * Deliberately NON-NUMERIC. Every value here is a plain-language judgment a
 * person makes about their own life ("Workable with changes"), never a 1-5
 * rating, never a weight, and never something that can be summed. There is no
 * scoring function anywhere in this tool because there is no honest way to
 * turn "money is a stretch" and "format conflicts" into one number that means
 * anything - the only ordering this tool produces is the user's own TIERS
 * below.
 *
 * Both this file and Views/Tools/ProgramShortlist.cshtml render these strings.
 * The Razor copy exists so the criteria are readable, crawlable content with
 * no JS - see src/tools/tests/razor-duplication-sync.test.js for the check
 * that keeps the two copies identical.
 */

export const CRITERIA = Object.freeze([
  {
    key: 'format',
    label: 'Format fit',
    helpText: 'How the program is delivered, against how you can actually study.',
    values: Object.freeze([
      { value: 'works', label: 'Fits how I need to study' },
      { value: 'workable', label: 'Workable with changes' },
      { value: 'conflicts', label: 'Conflicts with my life' },
    ]),
  },
  {
    key: 'money',
    label: 'Money',
    helpText: 'What you would have to commit, and how that sits with you.',
    values: Object.freeze([
      { value: 'manageable', label: 'Manageable commitment' },
      { value: 'stretch', label: 'A real stretch' },
      { value: 'out-of-reach', label: 'Out of reach as it stands' },
    ]),
  },
  {
    key: 'career',
    label: 'Career direction',
    helpText: 'Whether this program points where you are actually trying to go.',
    values: Object.freeze([
      { value: 'direct', label: 'Points where I am headed' },
      { value: 'partial', label: 'Partly relevant' },
      { value: 'off-track', label: 'Not the direction I want' },
    ]),
  },
  {
    key: 'access',
    label: 'Access',
    helpText: 'Whether you can realistically get there, get in, and stay enrolled.',
    values: Object.freeze([
      { value: 'open', label: 'Open to me' },
      { value: 'effortful', label: 'Possible with effort' },
      { value: 'barrier', label: 'A real barrier' },
    ]),
  },
  {
    key: 'effort',
    label: 'Effort to apply',
    helpText: 'What the application itself would cost you in time and energy.',
    values: Object.freeze([
      { value: 'light', label: 'Light lift' },
      { value: 'substantial', label: 'Substantial lift' },
      { value: 'too-much', label: 'More than I want to take on' },
    ]),
  },
]);

/**
 * The only ordering this tool has. Not a ranking and not derived from the
 * criteria above - a person moves a school between these three buckets by
 * hand, and "Ruled out" stays on the page on purpose so the reason a school
 * left the running is still visible next to the ones that stayed.
 */
export const TIERS = Object.freeze([
  { key: 'shortlist', label: 'Shortlist', helpText: 'Still in the running.' },
  { key: 'maybe', label: 'Maybe', helpText: 'Not ruled out, not committed.' },
  { key: 'ruled-out', label: 'Ruled out', helpText: 'Kept visible so you remember why.' },
]);

/** Where a newly added school lands: the neutral middle, never "Shortlist". */
export const DEFAULT_TIER = 'maybe';

const CRITERIA_BY_KEY = new Map(CRITERIA.map((c) => [c.key, c]));
const TIERS_BY_KEY = new Map(TIERS.map((t) => [t.key, t]));

export function isValidCriterionKey(key) {
  return typeof key === 'string' && CRITERIA_BY_KEY.has(key);
}

export function isValidCriterionValue(key, value) {
  const criterion = CRITERIA_BY_KEY.get(key);
  if (!criterion || typeof value !== 'string') return false;
  return criterion.values.some((v) => v.value === value);
}

export function criterionValueLabel(key, value) {
  const criterion = CRITERIA_BY_KEY.get(key);
  if (!criterion) return null;
  const match = criterion.values.find((v) => v.value === value);
  return match ? match.label : null;
}

export function isValidTier(tier) {
  return typeof tier === 'string' && TIERS_BY_KEY.has(tier);
}

export function tierLabel(tier) {
  const match = TIERS_BY_KEY.get(tier);
  return match ? match.label : null;
}
